<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, useAttrs } from 'vue';
defineOptions({ inheritAttrs: false, name: 'EGS_Popover' });
const emit = defineEmits<{ show: []; hide: [] }>();
const attrs = useAttrs();
const root = ref<HTMLElement | null>(null);
const visible = ref(false);
const position = ref({ left: '0px', top: '0px' });
const margin = 8;
const gap = 6;
let anchor: HTMLElement | null = null;
let resizeObserver: ResizeObserver | null = null;

function updatePosition() {
	if (!visible.value || !root.value) return;
	if (anchor && !anchor.isConnected) {
		hide();
		return;
	}
	const rect = anchor?.getBoundingClientRect();
	const panel = root.value.getBoundingClientRect();
	const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
	const viewportHeight = window.innerHeight;
	if (rect && (rect.bottom <= 0 || rect.top >= viewportHeight || rect.right <= 0 || rect.left >= viewportWidth)) {
		hide();
		return;
	}
	const below = (rect?.bottom ?? margin) + gap;
	const above = (rect?.top ?? margin) - gap - panel.height;
	const roomBelow = viewportHeight - margin - below;
	const roomAbove = (rect?.top ?? margin) - gap - margin;
	const top = panel.height > roomBelow && roomAbove > roomBelow ? above : below;
	position.value = {
		left: `${Math.max(margin, Math.min(rect?.left ?? margin, viewportWidth - panel.width - margin))}px`,
		top: `${Math.max(margin, Math.min(top, viewportHeight - panel.height - margin))}px`
	};
}
function stopTracking() {
	window.removeEventListener('scroll', updatePosition, true);
	window.removeEventListener('resize', updatePosition);
	resizeObserver?.disconnect();
	resizeObserver = null;
}
async function show(event: Event, target?: HTMLElement | null) {
	stopTracking();
	anchor = target ?? (event.currentTarget as HTMLElement | null) ?? (event.target as HTMLElement | null);
	visible.value = true;
	await nextTick();
	if (!visible.value || !root.value) return;
	updatePosition();
	if (!visible.value) return;
	window.addEventListener('scroll', updatePosition, true);
	window.addEventListener('resize', updatePosition);
	resizeObserver = new ResizeObserver(updatePosition);
	resizeObserver.observe(root.value);
	if (anchor) resizeObserver.observe(anchor);
	emit('show');
}
function hide() {
	if (!visible.value) return;
	visible.value = false;
	stopTracking();
	anchor = null;
	emit('hide');
}
function toggle(event: Event) {
	visible.value ? hide() : show(event);
}
function outside(event: MouseEvent) {
	if (root.value && !root.value.contains(event.target as Node)) hide();
}
defineExpose({ show, hide, toggle });
onMounted(() => document.addEventListener('mousedown', outside));
onBeforeUnmount(() => {
	document.removeEventListener('mousedown', outside);
	stopTracking();
});
</script>

<template>
	<Teleport to="body"
		><div v-if="visible" ref="root" v-bind="attrs" :style="position" :class="['egs-popover fixed z-[10000] rounded-xl border border-surface bg-[var(--egs-overlay-background)] p-4 text-color shadow-dropdown', attrs['class']]"><slot /></div
	></Teleport>
</template>

<style scoped>
.egs-popover {
	box-sizing: border-box;
	max-width: calc(100vw - 16px);
	max-height: calc(100dvh - 16px);
	overflow: auto;
	overscroll-behavior: contain;
}
</style>
